import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, Search, X, ChevronDown, Check, Tag, Dog, Cat, Sparkles, Leaf, Utensils } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
interface FilterState {
  category: string[];
  petType: string[];
  priceRange: [number, number];
  dietaryNeeds: string[];
  brand: string[];
  rating: number | null;
}
interface AdvancedProductFiltersProps {
  onFilterChange: (filters: FilterState) => void;
  onSearch: (query: string) => void;
  totalProducts: number;
  initialFilters?: Partial<FilterState>;
}
export function AdvancedProductFilters({
  onFilterChange,
  onSearch,
  totalProducts,
  initialFilters
}: AdvancedProductFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    category: [],
    petType: [],
    priceRange: [0, 200],
    dietaryNeeds: [],
    brand: [],
    rating: null,
    ...initialFilters
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    petType: true,
    category: true,
    price: true,
    dietary: false
  });
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, onSearch]);
  // Notify parent of filter changes
  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const toggleFilter = (key: keyof FilterState, value: string) => {
    setFilters(prev => {
      const current = prev[key] as string[];
      const updated = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
      return {
        ...prev,
        [key]: updated
      };
    });
  };
  const clearFilters = () => {
    setFilters({
      category: [],
      petType: [],
      priceRange: [0, 200],
      dietaryNeeds: [],
      brand: [],
      rating: null
    });
    setSearchQuery('');
    onSearch('');
  };
  const activeFiltersCount = filters.category.length + filters.petType.length + filters.dietaryNeeds.length + (filters.priceRange[0] > 0 || filters.priceRange[1] < 200 ? 1 : 0);
  return <div className="space-y-8" data-id="element-4015">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4016">
        <h3 className="text-lg font-bold text-charcoal-900 font-serif flex items-center gap-2" data-id="element-4017">
          <Filter className="w-5 h-5 text-primary-600" data-id="element-4018" />
          Filtros
        </h3>
        {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-xs font-bold text-primary-600 hover:text-primary-800 hover:underline" data-id="element-4019">
            Limpiar todo
          </button>}
      </div>

      {/* Search */}
      <div className="relative" data-id="element-4020">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-4021" />
        <input type="text" placeholder="Buscar productos..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" data-id="element-4022" />
      </div>

      {/* Pet Type Filter */}
      <div className="border-b border-gray-100 pb-6" data-id="element-4023">
        <button onClick={() => toggleSection('petType')} className="flex items-center justify-between w-full mb-4 group" data-id="element-4024">
          <span className="font-bold text-charcoal-800 text-sm" data-id="element-4025">Mascota</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${expandedSections.petType ? 'rotate-180' : ''}`} data-id="element-4026" />
        </button>

        <AnimatePresence data-id="element-4027">
          {expandedSections.petType && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden" data-id="element-4028">
              <div className="grid grid-cols-2 gap-3" data-id="element-4029">
                <button onClick={() => toggleFilter('petType', 'dog')} className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${filters.petType.includes('dog') ? 'border-primary-600 bg-primary-50 text-primary-800' : 'border-gray-100 hover:border-gray-200 text-charcoal-600'}`} data-id="element-4030">
                  <Dog className="w-5 h-5" data-id="element-4031" />
                  <span className="font-bold text-sm" data-id="element-4032">Perro</span>
                </button>
                <button onClick={() => toggleFilter('petType', 'cat')} className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${filters.petType.includes('cat') ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-100 hover:border-gray-200 text-charcoal-600'}`} data-id="element-4033">
                  <Cat className="w-5 h-5" data-id="element-4034" />
                  <span className="font-bold text-sm" data-id="element-4035">Gato</span>
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Categories */}
      <div className="border-b border-gray-100 pb-6" data-id="element-4036">
        <button onClick={() => toggleSection('category')} className="flex items-center justify-between w-full mb-4 group" data-id="element-4037">
          <span className="font-bold text-charcoal-800 text-sm" data-id="element-4038">Categoría</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${expandedSections.category ? 'rotate-180' : ''}`} data-id="element-4039" />
        </button>

        <AnimatePresence data-id="element-4040">
          {expandedSections.category && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden space-y-2" data-id="element-4041">
              {[{
            id: 'food',
            label: 'Alimentación',
            icon: Utensils
          }, {
            id: 'snacks',
            label: 'Snacks Naturales',
            icon: Sparkles
          }, {
            id: 'supplements',
            label: 'Suplementos',
            icon: Leaf
          }, {
            id: 'toys',
            label: 'Juguetes',
            icon: Tag
          }].map(cat => <label key={cat.id} className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition-colors" data-id="element-4042">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${filters.category.includes(cat.id) ? 'bg-primary-600 border-primary-600' : 'border-gray-300 bg-white'}`} data-id="element-4043">
                    {filters.category.includes(cat.id) && <Check className="w-3 h-3 text-white" data-id="element-4044" />}
                  </div>
                  <input type="checkbox" className="hidden" checked={filters.category.includes(cat.id)} onChange={() => toggleFilter('category', cat.id)} data-id="element-4045" />
                  <span className={`text-sm ${filters.category.includes(cat.id) ? 'font-bold text-charcoal-900' : 'text-charcoal-600'}`} data-id="element-4046">
                    {cat.label}
                  </span>
                </label>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Price Range */}
      <div className="border-b border-gray-100 pb-6" data-id="element-4047">
        <button onClick={() => toggleSection('price')} className="flex items-center justify-between w-full mb-4 group" data-id="element-4048">
          <span className="font-bold text-charcoal-800 text-sm" data-id="element-4049">Precio</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${expandedSections.price ? 'rotate-180' : ''}`} data-id="element-4050" />
        </button>

        <AnimatePresence data-id="element-4051">
          {expandedSections.price && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden px-1" data-id="element-4052">
              <div className="flex items-center justify-between mb-4 text-sm font-bold text-charcoal-600" data-id="element-4053">
                <span data-id="element-4054">{filters.priceRange[0]}€</span>
                <span data-id="element-4055">{filters.priceRange[1]}€+</span>
              </div>
              <input type="range" min="0" max="200" step="5" value={filters.priceRange[1]} onChange={e => setFilters(prev => ({
            ...prev,
            priceRange: [prev.priceRange[0], parseInt(e.target.value)]
          }))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600" data-id="element-4056" />
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Dietary Needs */}
      <div className="pb-6" data-id="element-4057">
        <button onClick={() => toggleSection('dietary')} className="flex items-center justify-between w-full mb-4 group" data-id="element-4058">
          <span className="font-bold text-charcoal-800 text-sm" data-id="element-4059">
            Necesidades Especiales
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${expandedSections.dietary ? 'rotate-180' : ''}`} data-id="element-4060" />
        </button>

        <AnimatePresence data-id="element-4061">
          {expandedSections.dietary && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden flex flex-wrap gap-2" data-id="element-4062">
              {['Sin cereales', 'Hipoalergénico', 'Control de peso', 'Senior', 'Cachorro'].map(tag => <button key={tag} onClick={() => toggleFilter('dietaryNeeds', tag)} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${filters.dietaryNeeds.includes(tag) ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-gray-200 text-charcoal-600 hover:border-gray-300'}`} data-id="element-4063">
                  {tag}
                </button>)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}